/**
 * Where the SIP server and the ICE servers live.
 *
 * These used to be hardcoded: the previous vendor's Asterisk for SIP and
 * Google's public STUN servers for ICE. A hospital on a private network can
 * reach neither, so calling could not work there at all — and the vendor's
 * host is the wrong place for a different operator's traffic in any case.
 *
 * Both now come from `window._env_`, written at container start by
 * `docker/30-render-env-js.sh`, so one image serves any site. On this branch
 * there are no internet fallbacks at all: an unset value yields an empty
 * one, so a misconfigured deployment fails visibly instead of quietly
 * contacting a host outside the hospital network.
 */
const env = (): Record<string, string> => (window as any)._env_ || {};

// No fallback to a public STUN server on this branch. A deployment that
// forgets to set STUN_SERVERS gets host candidates only -- which works on a
// flat LAN -- rather than silently reaching out to Google, which is exactly
// what an air-gapped install must never do.
const DEFAULT_STUN = "";

export const sipServer = (): string => env().SIP_SERVER || "";

export const sipWssPort = (): string => env().SIP_WSS_PORT || "8089";

export const sipDomain = (): string => env().SIP_DOMAIN || sipServer();

export const sipWebSocketUrl = (): string => `wss://${sipServer()}:${sipWssPort()}/ws`;

/**
 * ICE servers as RTCPeerConnection wants them. STUN_SERVERS is a comma
 * separated list so a site can add its own TURN server beside STUN without a
 * rebuild; an entry may carry credentials as `url|user|password`.
 */
export const iceServers = (): RTCIceServer[] =>
  (env().STUN_SERVERS || DEFAULT_STUN)
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [urls, username, credential] = entry.split("|");
      return username ? { urls, username, credential } : { urls };
    });

/** True when this deployment has no route to the public internet. */
export const isOfflineDeployment = (): boolean => env().OFFLINE_MODE === "true";
